import ClientProfiler from '@zillow/client-profiler-core';
import getBrowserLogger from '@zg-rentals/logger-browser';
import type { BrowserLoggerOptions, Logger } from '@zg-rentals/logger-browser';
import type { DatadogPluginConfig } from '@zg-rentals/monitor-browser';
import type { MonitorPlugin, MonitorReporter } from '@zg-rentals/monitor-base';
import type { RpBootstrapOptions } from '@zg-rentals/rp-bootstrap-base';
import { BrowserTracer } from '@zg-rentals/trace-browser';
import { getCurrentEnvForClientSide, PRODUCTION, STAGING } from '@zg-rentals/rental-platform-config';
import { getDefaults, RpBootstrap } from '@zg-rentals/rp-bootstrap-base';
import { isDev, isTest } from '@zg-rentals/environment-utils';
import { logError } from '@zg-rentals/log-error';
import { setAppInfo } from '@zg-rentals/app-info';

import {
  BrowserLogReporter,
  BrowserMonitor,
  BrowserMonitorPontoonReporter,
  DatadogPlugin,
} from '@zg-rentals/monitor-browser';

type RpBrowserFeatureFlags = {
  useNewErrorLogging?: boolean;
  useMarlinWithoutProxy?: boolean;
  useClientProfiler?: boolean;
};

export type RpBootstrapBrowserOptions = {
  loggerOptions?: BrowserLoggerOptions;
  featureFlags?: RpBrowserFeatureFlags;
  monitorOptions?: {
    reporters?: Array<(logger: Logger) => MonitorReporter>;
    plugins?: Array<(logger: Logger) => MonitorPlugin>;
    proxyPath?: string;
    datadogPluginConfig?: DatadogPluginConfig;
  };
} & RpBootstrapOptions;

export class RpBootstrapBrowser extends RpBootstrap {
  private options?: RpBootstrapBrowserOptions;
  private logger?: Logger;
  private monitor?: BrowserMonitor;
  private tracer?: BrowserTracer;
  private isBrowser: boolean;

  constructor() {
    super();
    this.isBrowser = typeof window !== 'undefined';
  }

  public bootstrap(options: RpBootstrapBrowserOptions) {
    if (this.isInitialized) {
      // eslint-disable-next-line no-console
      console.debug('RpBootstrapBrowser: already bootstrapped');
      return this;
    }

    this.isInitialized = true;

    this.options = options;
    const appInfo = this.getInfo();

    this.initLogger();
    this.initMonitor();
    this.initTracer();

    if (this.isBrowser && this.options?.featureFlags?.useClientProfiler) {
      const environment = getCurrentEnvForClientSide();

      // @ts-ignore zillow typescript not great, eh?
      const clientProfiler = new ClientProfiler({
        staticDimensions: {
          buildNumber: appInfo.buildNumber,
        },
      });

      clientProfiler.registerPage({
        pageName: appInfo.appName,
        environment: environment === PRODUCTION ? 'production' : environment === STAGING ? 'staging' : 'development',
        platform: 'desktop',
      });
    }

    if (!isTest()) {
      this.logger?.debug('bootstrapped' + JSON.stringify(appInfo));
    }

    setAppInfo({ ...appInfo });

    this.configureGlobals(this.logger!, this.monitor!, this.tracer!);

    if (options.featureFlags?.useNewErrorLogging) {
      this.monitor!.count({ name: `browser init` });
      this.addErrorEventListeners();
    }

    return this;
  }

  public getLogger(name?: string): Logger {
    if (!this.logger) {
      throw new Error('RpBootstrapBrowser: logger not initialized');
    }
    if (!name) {
      return this.logger;
    }
    return this.logger.child({ name });
  }

  public getMonitor(): BrowserMonitor {
    if (!this.monitor) {
      throw new Error('RpBootstrapBrowser: monitor not initialized');
    }
    return this.monitor;
  }

  public getTracer(): BrowserTracer {
    if (!this.tracer) {
      throw new Error('RpBootstrapBrowser: tracer not initialized');
    }
    return this.tracer;
  }

  public getInfo() {
    if (!this.options) {
      throw new Error('RpBootstrapBrowser: options not initialized');
    }
    return {
      appName: this.options.appName,
      buildNumber: this.options.buildNumber,
    };
  }

  public reset() {
    super.reset();
    this.logger = undefined;
    this.tracer = undefined;
    this.monitor = undefined;
    this.options = undefined;
  }

  private initLogger() {
    const { loggerOptions = {} } = this.options!;
    const { logPath, ...loggerOptionsRest } = loggerOptions;
    const newLogPath = isDev() ? undefined : logPath;

    this.logger = getBrowserLogger({
      name: this.options!.appName,
      ...loggerOptionsRest,
      logPath: newLogPath,
    });

    if (this.isBrowser) {
      // @ts-ignore
      window.log = this.logger;
    }
  }

  private initMonitor() {
    const reporters: Array<MonitorReporter> = [];
    const plugins: Array<MonitorPlugin> = [];
    if (this.isBrowser) {
      reporters.push(new BrowserLogReporter({ logger: this.logger }));
      if (this.options?.monitorOptions?.reporters) {
        reporters.push(...this.options.monitorOptions.reporters.map((r) => r(this.logger!)));
      }
      if (this.options?.monitorOptions?.plugins) {
        plugins.push(...this.options.monitorOptions.plugins.map((p) => p(this.logger!)));
      }

      if (this.options?.monitorOptions?.datadogPluginConfig && getCurrentEnvForClientSide() === PRODUCTION) {
        plugins.push(
          new DatadogPlugin({
            logger: this.getLogger('datadogPlugin'),
            datadogOptions: {
              service: this.options.appName,
              version: `${this.options.buildNumber}`,
              // spread at the end to allow for overrides
              ...this.options.monitorOptions.datadogPluginConfig.datadogOptions,
            },
          }),
        );
      }
      const proxyPath = this.options?.monitorOptions?.proxyPath;
      const useMarlinWithoutProxy = this.options?.featureFlags?.useMarlinWithoutProxy;
      if (proxyPath || useMarlinWithoutProxy) {
        reporters.push(
          new BrowserMonitorPontoonReporter({
            logger: this.getLogger('pontoon-proxy'),
            proxyPath,
            marlinOpts: {
              useCrossOrigin: useMarlinWithoutProxy,
              appName: this.options?.appName,
            },
          }),
        );
      }
    }

    this.monitor = new BrowserMonitor({
      logger: this.logger,
      reporters,
      plugins,
    });

    if (this.isBrowser) {
      this.monitor.count({ name: 'browser.initialized' });
    }
  }

  private initTracer() {
    if (this.isBrowser) {
      this.tracer = new BrowserTracer(this.monitor!);
      this.tracer.init();
    }
  }

  private addErrorEventListeners() {
    if (this.isBrowser) {
      window.addEventListener('error', (errorEvent) => {
        const { error, filename, lineno, colno } = errorEvent;
        logError({
          error,
          context: {
            filename: filename,
            lineNumber: lineno,
            columnNumber: colno,
          },
        });
      });
      window.addEventListener('unhandledrejection', (e) => {
        logError({
          error: e.reason,
          errorType: 'unhandledRejection',
        });
      });
    }
  }
}

let rpBootstrap = new RpBootstrapBrowser();

let isInitializedDefault = false;

export function bootstrap(options: RpBootstrapBrowserOptions) {
  if (isInitializedDefault) {
    rpBootstrap = new RpBootstrapBrowser();
  }
  isInitializedDefault = false;

  const defaults = getDefaults();

  return rpBootstrap.bootstrap({
    loggerOptions: {
      level: defaults.logLevel ?? 'info',
    },
    ...defaults,
    ...options,
  });
}

export function getDefaultBootstrap(overrides?: Partial<RpBootstrapBrowserOptions>) {
  if (rpBootstrap.isInitialized) {
    return rpBootstrap;
  }

  isInitializedDefault = true;

  const { appName, buildNumber, logLevel } = getDefaults();

  return rpBootstrap.bootstrap({
    appName: appName ?? 'unknown',
    buildNumber: buildNumber,
    loggerOptions: {
      level: logLevel ?? 'info',
      ...overrides?.loggerOptions,
    },
    ...overrides,
  });
}
